import useAuthStore from '../store/authStore';

/**
 * usePermissions
 * --------------
 * Centralized permission rules for the per-center detail-page surface
 * (LearningStudentPage / TherapyClientPage / HairClientPage and the modals
 * they own).
 *
 * Why this exists:
 *   - Today `user.role` is read directly in 13+ files with ad-hoc checks.
 *   - When a rule changes ("teachers can edit billing"), it has to change
 *     in every one of those files.
 *   - This hook owns every rule for the new detail-page surface so the JSX
 *     stays clean and rule changes happen in exactly one place.
 *
 * Roles in this codebase: admin | manager | center_manager | teacher | student
 *
 * Scope:
 *   - This hook is *only* adopted inside the new detail-page surface.
 *   - The other 13 files that read `user.role` directly are intentionally
 *     out of scope. No risky sweeping refactor.
 *
 * Usage:
 *
 *   import { usePermissions } from '../hooks/usePermissions';
 *   import Authorize from '../components/Authorize';
 *
 *   const perms = usePermissions();
 *
 *   <InfoCard
 *     title="Contact"
 *     action={
 *       <Authorize when={perms.canEditStudentProfile}>
 *         <Button onClick={openEditContact}>Edit</Button>
 *       </Authorize>
 *     }
 *   />
 *
 *   // Function-style rule (per-row):
 *   const note = ...;
 *   <Authorize when={perms.canEditOwnNote(note)}>
 *     <button>Edit</button>
 *   </Authorize>
 */

export function usePermissions() {
  const user = useAuthStore((state) => state.user);
  const centerType = useAuthStore((state) => state.centerType);
  const role = user?.role;

  const isAdmin   = role === 'admin';
  const isManager = isAdmin || role === 'manager' || role === 'center_manager';
  const isTeacher = role === 'teacher';
  const isWorker = isTeacher;
  const isConsultant = role === 'consultant';
  // Bulk WhatsApp: managers and super admins always; consultants and workers
  // only on hair-transplant centers.
  const canSendBulkWhatsApp = isManager || role === 'super_admin' || ((isConsultant || isWorker) && centerType === 'hair_transplant');

  return {
    // Raw context (use sparingly — prefer the boolean flags below)
    role,
    isAdmin,
    isManager,
    isTeacher,
    isWorker,

    // -- Profile (all three center types) ----------------------------------
    canEditStudentProfile: isManager,
    canDeleteStudent:      isManager,
    canArchiveStudent:     isManager,

    // -- Notes -------------------------------------------------------------
    // Anyone in the center can author a note.
    canAddNote: !!user,
    // Authors can edit/delete their own notes; managers can edit any.
    canEditOwnNote:   (note) => isManager || (note && note.authorId === user?.id),
    canDeleteOwnNote: (note) => isManager || (note && note.authorId === user?.id),

    // -- Learning center ---------------------------------------------------
    canManageBilling:    isManager,         // jibaya add/edit/void
    canMarkAttendance:   isManager || isTeacher,
    canManageGroups:     isManager,         // add/remove from group
    canAssignChecklist:  isManager,
    canAssignSurvey:     isManager,
    canManageHomework:   isManager || isTeacher,

    // -- Therapy center ----------------------------------------------------
    canScheduleSession:  isManager || isTeacher,
    canCancelSession:    isManager,
    // Teachers (therapists) can author clinical notes; only managers can
    // edit a note that has already been signed.
    canAddClinicalNote:  isManager || isTeacher,
    canSignClinicalNote: isManager || isTeacher,
    canEditSignedNote:   isManager,
    canUploadDocument:   isManager || isTeacher,
    canDeleteDocument:   isManager,

    // -- Hair-transplant center --------------------------------------------
    canEditMedicalBrief: isManager,
    canEditHairAnalysis: isManager,
    canEditTreatmentPlan: isManager,
    canSendQuote:         isManager,
    canEditFollowUp:      isManager || isTeacher,
    canCancelClient:      isManager,

    // -- Enterprise CRM (Phase 1) ------------------------------------------
    canManageWorkers:     isManager,         // CRUD on worker profiles
    canAssignStaff:       isManager,         // Assign consultant/specialist to clients

    // -- Bulk WhatsApp ------------------------------------------------------
    canSendBulkWhatsApp,

    // -- Therapy worker/manager surface (Item 1 of therapy TDD plan) -------
    // Managers see every client; workers are limited to their assignments.
    canViewAllClients:       isManager,
    canViewAssignedClients:  !!user,

    // Guest (non-system) client workflow — both roles can create, only
    // managers can promote a guest to a full client (owns lifecycle).
    canCreateGuestMeeting:   isManager || isTeacher,
    canPromoteGuestClient:   isManager,

    // Survey sharing — only managers can send surveys; any authenticated
    // user can view the responses collected on a client's page.
    canShareSurveyViaWhatsApp: isManager,
    canViewSurveyResponses:    !!user,
  };
}

export default usePermissions;
